import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useMapStore } from '../stores/useMapStore';
import { useUiStore } from '../stores/useUiStore';
import { useGeologyStore } from '../stores/useGeologyStore';
import { useProceduralStore } from '../stores/useProceduralStore';
import { useGeologyQuery } from '../hooks/useGeologyQuery';
import { SearchBar } from './SearchBar';
import { PresetLocations } from './PresetLocations';
import { BboxOverlay } from './BboxOverlay';
import { PolygonDraw } from './PolygonDraw';
import { SelectionModeToggle } from './SelectionModeToggle';
import { GenerateTerrainButton } from './GenerateTerrainButton';
import { GeologyPanel } from './GeologyPanel';
import { generateProceduralTerrain } from '../lib/proceduralGenerator';
import type { Map as MapLibreMap } from 'maplibre-gl';

// Lazy-load MapLibre to reduce initial bundle
let maplibrePromise: Promise<typeof import('maplibre-gl')> | null = null;
function loadMapLibre() {
  if (!maplibrePromise) {
    maplibrePromise = import('maplibre-gl').catch((err) => {
      maplibrePromise = null;
      throw err;
    });
  }
  return maplibrePromise;
}

const CARTO_DARK_STYLE = {
  version: 8 as const,
  sources: {
    'raster-tiles': {
      type: 'raster' as const,
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    },
  },
  layers: [
    {
      id: 'simple-tiles',
      type: 'raster' as const,
      source: 'raster-tiles',
      minzoom: 0,
      maxzoom: 22,
    },
  ],
};

const BboxAreaLabel: React.FC<{ bbox: [number, number, number, number]; centerLat: number }> = React.memo(({ bbox, centerLat }) => {
  const label = useMemo(() => {
    const area = (bbox[2] - bbox[0]) * (bbox[3] - bbox[1]) * 111.32 * 111.32 * Math.cos(centerLat * Math.PI / 180);
    if (!isFinite(area) || area < 0) return '—';
    return area < 1 ? `${(area * 100).toFixed(1)} ha` : `${area.toFixed(2)} km²`;
  }, [bbox, centerLat]);

  return (
    <div className="rounded bg-slate-950/70 px-2 py-0.5 text-[10px] font-mono text-sky-400 border border-slate-800/50">
      {label}
    </div>
  );
});

const OSM_FALLBACK_STYLE = {
  version: 8 as const,
  sources: {
    'raster-tiles': {
      type: 'raster' as const,
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    },
  },
  layers: [
    {
      id: 'simple-tiles',
      type: 'raster' as const,
      source: 'raster-tiles',
      minzoom: 0,
      maxzoom: 19,
    },
  ],
};

export const MapViewport: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [tileError, setTileError] = useState(false);
  const hasSwitchedToFallback = useRef(false);

  const center = useMapStore((s) => s.center);
  const zoom = useMapStore((s) => s.zoom);
  const bbox = useMapStore((s) => s.bbox);
  const flyTo = useMapStore((s) => s.flyTo);
  const setCenter = useMapStore((s) => s.setCenter);
  const setZoom = useMapStore((s) => s.setZoom);
  // selectionMode is used by child components via store
  const setViewMode = useUiStore((s) => s.setViewMode);
  const setProceduralGenerating = useProceduralStore((s) => s.setGenerating);
  const setProceduralRaw = useProceduralStore((s) => s.setRawTerrain);
  const setProceduralSceneId = useProceduralStore((s) => s.setSceneId);
  const isGenerating = useProceduralStore((s) => s.isGenerating);
  const generationProgress = useProceduralStore((s) => s.generationProgress);
  const generationError = useProceduralStore((s) => s.generationError);
  const setGenerationError = useProceduralStore((s) => s.setGenerationError);

  // Geology query — sync to store via getState to avoid subscription loop
  const { data: geologyData, isLoading: geologyLoading, error: geologyError } = useGeologyQuery(bbox);

  useEffect(() => {
    if (geologyLoading) {
      useGeologyStore.getState().setLoading(true);
    } else if (geologyError) {
      useGeologyStore.getState().setError(geologyError instanceof Error ? geologyError.message : 'Failed to fetch geology');
    } else if (geologyData) {
      useGeologyStore.getState().setGeology({
        unitCode: geologyData.unit_code,
        unitName: geologyData.unit_name,
        rockGroup: geologyData.rock_group,
        stratAge: geologyData.strat_age,
        stratName: geologyData.strat_name,
      });
    }
  }, [geologyData, geologyLoading, geologyError]);

  // Initialize MapLibre
  useEffect(() => {
    let map: MapLibreMap | null = null;
    let cancelled = false;
    let injectedLink: HTMLLinkElement | null = null;

    async function initMap() {
      let ml: typeof import('maplibre-gl');
      try {
        ml = await loadMapLibre();
      } catch (err) {
        console.error('Failed to load MapLibre:', err);
        return;
      }
      if (cancelled || !mapContainerRef.current) return;

      // Inject MapLibre CSS dynamically
      const linkId = 'maplibre-css';
      if (!document.getElementById(linkId)) {
        const link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/maplibre-gl@5.3.0/dist/maplibre-gl.css';
        document.head.appendChild(link);
        injectedLink = link;
      }

      map = new ml.Map({
        container: mapContainerRef.current,
        style: CARTO_DARK_STYLE,
        center: [center[0], center[1]],
        zoom: zoom,
        attributionControl: false,
      });

      map.on('load', () => {
        if (cancelled) return;
        setMapLoaded(true);
        setTileError(false);
        hasSwitchedToFallback.current = false;
        map!.addControl(
          new ml.AttributionControl({ compact: true }),
          'bottom-left'
        );
        map!.addControl(new ml.NavigationControl({}), 'top-right');
        map!.addControl(new ml.FullscreenControl({}), 'top-right');
        map!.addControl(
          new ml.GeolocateControl({
            positionOptions: { enableHighAccuracy: true },
            trackUserLocation: true,
          }),
          'top-right'
        );
        map!.addControl(new ml.ScaleControl({}), 'bottom-right');
      });

      map.on('moveend', () => {
        if (cancelled || !map) return;
        const c = map.getCenter();
        setCenter([c.lng, c.lat]);
        setZoom(map.getZoom());
      });

      map.on('style.load', () => {
        if (!cancelled) setTileError(false);
      });

      map.on('error', (e: any) => {
        console.warn('MapLibre error:', e);
        const msg = String(e.error?.message ?? '');
        const isTileError =
          e.error?.status === 404 ||
          e.error?.status >= 500 ||
          msg.toLowerCase().includes('tile') ||
          msg.toLowerCase().includes('network') ||
          msg.toLowerCase().includes('fetch') ||
          msg.toLowerCase().includes('failed to load');
        if (isTileError && !hasSwitchedToFallback.current) {
          hasSwitchedToFallback.current = true;
          setTileError(true);
          if (map) {
            try {
              map.setStyle(OSM_FALLBACK_STYLE);
            } catch (styleErr) {
              console.error('OSM fallback failed:', styleErr);
            }
          }
        }
      });

      mapRef.current = map;
    }

    initMap();

    return () => {
      cancelled = true;
      if (injectedLink) {
        injectedLink.remove();
      }
      if (map) {
        map.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Respond to store-driven flyTo
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    const current = map.getCenter();
    const currentZoom = map.getZoom();
    const dx = Math.abs(current.lng - center[0]);
    const dy = Math.abs(current.lat - center[1]);
    const dz = Math.abs(currentZoom - zoom);
    if (dx > 0.0001 || dy > 0.0001 || dz > 0.1) {
      map.flyTo({ center: [center[0], center[1]], zoom, essential: true });
    }
  }, [center, zoom, mapLoaded]);

  const setProceduralProgress = useProceduralStore((s) => s.setGenerationProgress);

  const handleGenerate = useCallback(() => {
    if (!bbox) return;
    setGenerationError(null);
    setProceduralGenerating(true);
    setProceduralProgress(0.1);

    // Run generation async
    requestAnimationFrame(() => {
      try {
        const centerLng = (bbox[0] + bbox[2]) / 2;
        const centerLat = (bbox[1] + bbox[3]) / 2;

        setProceduralProgress(0.4);
        // Generate terrain
        const raw = generateProceduralTerrain(centerLng, centerLat, 40);
        setProceduralProgress(0.8);
        setProceduralRaw(raw);
        setProceduralSceneId(`procedural-${Date.now()}`);
        setProceduralProgress(1.0);

        // Switch to 3D view
        setViewMode('3d');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Procedural generation failed';
        console.error('Procedural generation failed:', err);
        setGenerationError(msg);
      } finally {
        setProceduralGenerating(false);
        setProceduralProgress(0);
      }
    });
  }, [bbox, setGenerationError, setProceduralGenerating, setProceduralProgress, setProceduralRaw, setProceduralSceneId, setViewMode]);

  return (
    <div className="relative h-full w-full bg-surface">
      {/* Search + Presets overlay */}
      <div className="absolute left-4 right-4 top-4 z-10 flex flex-col gap-2 sm:left-4 sm:right-auto sm:min-w-[320px]">
        <SearchBar onSelect={(lng, lat, zoomLevel) => flyTo([lng, lat], zoomLevel)} />
        <PresetLocations onSelect={(lng, lat, zoomLevel) => flyTo([lng, lat], zoomLevel)} />
      </div>

      {/* Selection mode toggle (top-right) */}
      <div className="absolute right-4 top-4 z-10">
        <SelectionModeToggle />
      </div>

      {/* Map container */}
      <div
        ref={mapContainerRef}
        className="h-full w-full"
        style={{ touchAction: 'none' }}
        role="application"
        aria-label="Interactive map. Use arrow keys to pan, plus and minus to zoom."
        tabIndex={0}
      />

      {/* BBox overlay */}
      {mapLoaded && (
        <BboxOverlay mapRef={mapRef} containerRef={mapContainerRef} />
      )}

      {/* Polygon drawing */}
      <PolygonDraw mapRef={mapRef} mapLoaded={mapLoaded} />

      {/* Generate terrain button */}
      <GenerateTerrainButton onGenerate={handleGenerate} isGenerating={isGenerating} />

      {/* Generation progress overlay */}
      {isGenerating && (
        <div className="absolute bottom-16 left-1/2 z-20 w-64 -translate-x-1/2 rounded-lg bg-slate-900/90 px-4 py-3 backdrop-blur-sm border border-slate-700/50 shadow-lg">
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="text-slate-300">Generating terrain…</span>
            <span className="font-mono text-sky-300">{Math.round(generationProgress * 100)}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
            <div
              className="h-full rounded-full bg-sky-400 transition-all duration-200"
              style={{ width: `${Math.max(5, generationProgress * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Geology panel (bottom-left, above attribution) */}
      {mapLoaded && (
        <div className="absolute bottom-10 left-4 z-10 max-w-[280px]">
          <GeologyPanel />
        </div>
      )}

      {/* Loading state */}
      {!mapLoaded && (
        <div className="absolute inset-0 flex flex-col bg-surface">
          {/* Skeleton header bar */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="h-9 w-64 rounded-lg animate-skeleton" />
            <div className="h-9 w-32 rounded-lg animate-skeleton" />
          </div>
          {/* Skeleton map area */}
          <div className="flex-1 m-2 rounded-xl animate-skeleton opacity-50" />
          {/* Skeleton bottom bar */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="h-6 w-48 rounded animate-skeleton" />
            <div className="h-6 w-24 rounded animate-skeleton" />
          </div>
        </div>
      )}

      {/* Live coordinate HUD */}
      {mapLoaded && (
        <div className="absolute bottom-2 right-2 z-10 flex flex-col items-end gap-0.5">
          <div className="rounded bg-slate-950/70 px-2 py-0.5 text-[10px] font-mono text-slate-400 border border-slate-800/50">
            {center[1].toFixed(5)}°N, {center[0].toFixed(5)}°E
          </div>
          {bbox && <BboxAreaLabel bbox={bbox} centerLat={center[1]} />}
        </div>
      )}

      {/* Generation error */}
      {generationError && (
        <div className="absolute bottom-32 left-4 z-10 rounded-lg bg-red-900/80 px-3 py-2 text-xs text-red-200 backdrop-blur-sm border border-red-700/50">
          {generationError}
        </div>
      )}

      {/* Tile error warning */}
      {tileError && (
        <div className="absolute bottom-20 left-4 z-10 rounded-lg bg-amber-900/80 px-3 py-2 text-xs text-amber-200 backdrop-blur-sm border border-amber-700/50">
          Primary tiles unavailable — using fallback
        </div>
      )}
    </div>
  );
};
